import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext'; // ✅ Added Auth Context for Refresh
import axiosClient from '../../api/axiosClient';
import { 
  User, Save, Shield, Smartphone, CheckCircle, 
  Key, X, Loader2 
} from 'lucide-react';

const UserProfilePage = () => {
  const queryClient = useQueryClient();
  const { loginWithToken } = useAuth(); // To refresh global context after save
  const [activeTab, setActiveTab] = useState('details'); 
  
  // --- STATE FOR PROFILE ---
  const [formData, setFormData] = useState({
    full_name: '', email: '', mobile_number: '', company_name: '', 
    address_line_1: '', city: '', state: '', country: '', pincode: ''
  });

  // --- STATE FOR SECURITY ---
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null); 
  const [otp, setOtp] = useState('');

  // 1️⃣ FETCH PROFILE (Robust)
  const { data: profile, isLoading } = useQuery({
    queryKey: ['userProfile'],
    queryFn: async () => {
      const res = await axiosClient.get('/user/profile');
      // Handle axios interceptor variations (res vs res.data)
      return res.data || res; 
    }
  });

  // ✅ FIX: Force Update Form when Data Arrives
  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        email: profile.email || '',
        mobile_number: profile.mobile_number || '',
        company_name: profile.company_name || '',
        address_line_1: profile.address_line_1 || '',
        city: profile.city || '',
        state: profile.state || '',
        country: profile.country || '',
        pincode: profile.pincode || ''
      });
    }
  }, [profile]);

  // 2️⃣ MUTATION: UPDATE PROFILE
  const updateProfileMutation = useMutation({
    mutationFn: (data) => axiosClient.put('/user/profile', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['userProfile']);
      
      // ✅ Refresh Global Context (Updates Sidebar Name immediately)
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      if (token) loginWithToken(token);

      alert("Profile Updated Successfully!");
    },
    onError: (err) => alert("Update failed: " + (err.response?.data?.detail || err.message))
  });

  // 3️⃣ MUTATION: CHANGE PASSWORD
  const changePasswordMutation = useMutation({
    mutationFn: (data) => axiosClient.post('/user/change-password', data),
    onSuccess: () => {
      setPasswordData({ current: '', new: '', confirm: '' });
      alert("Password Changed Successfully!");
    },
    onError: (err) => alert("Password change failed: " + (err.response?.data?.detail || "Check input"))
  });

  // 4️⃣ MUTATION: SETUP 2FA
  const setup2FAMutation = useMutation({
    mutationFn: () => axiosClient.post('/user/security/2fa/setup'),
    onSuccess: (res) => {
      // Handle axios wrapping
      const data = res.data || res;
      setQrCodeData(data);
      setShow2FAModal(true);
    }
  });

  // 5️⃣ MUTATION: ENABLE 2FA
  const enable2FAMutation = useMutation({
    mutationFn: (data) => axiosClient.post('/user/security/2fa/enable', data),
    onSuccess: () => {
      setShow2FAModal(false);
      setQrCodeData(null);
      setOtp('');
      queryClient.invalidateQueries(['userProfile']);
      alert("2FA is now ACTIVE.");
    },
    onError: () => alert("Invalid OTP Code")
  });

  // 6️⃣ MUTATION: DISABLE 2FA
  const disable2FAMutation = useMutation({
    mutationFn: () => axiosClient.post('/user/security/2fa/disable'),
    onSuccess: () => {
      queryClient.invalidateQueries(['userProfile']);
      alert("2FA has been disabled.");
    }
  });

  if (isLoading) return <div className="p-20 text-center text-slate-500"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-2"/>Loading Profile...</div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in pb-10">
      
      {/* Header */}
      <div className="flex items-center space-x-4 mb-6">
        <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold uppercase">
          {profile?.full_name?.charAt(0) || 'U'}
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{profile?.full_name || 'User'}</h1>
          <p className="text-slate-500 text-sm flex items-center">
            <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-bold uppercase mr-2">{profile?.role || 'Staff'}</span>
            {profile?.email}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        <button 
          onClick={() => setActiveTab('details')}
          className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'details' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          <User className="w-4 h-4"/> Personal Details
        </button>
        <button 
          onClick={() => setActiveTab('security')}
          className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'security' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          <Shield className="w-4 h-4"/> Security & 2FA
        </button>
      </div>

      {/* --- TAB 1: PERSONAL DETAILS --- */}
      {activeTab === 'details' && (
        <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm animate-in fade-in">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Full Name</label>
              <input className="w-full p-2.5 border rounded-lg" value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Email (Read Only)</label>
              <input className="w-full p-2.5 border rounded-lg bg-slate-50 text-slate-500 cursor-not-allowed" value={formData.email} disabled />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Mobile Number</label>
              <input className="w-full p-2.5 border rounded-lg" value={formData.mobile_number} onChange={e => setFormData({...formData, mobile_number: e.target.value})} />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Company</label>
              <input className="w-full p-2.5 border rounded-lg" value={formData.company_name} onChange={e => setFormData({...formData, company_name: e.target.value})} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Address</label>
              <input className="w-full p-2.5 border rounded-lg" value={formData.address_line_1} onChange={e => setFormData({...formData, address_line_1: e.target.value})} />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">City</label>
              <input className="w-full p-2.5 border rounded-lg" value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Country</label>
              <input className="w-full p-2.5 border rounded-lg" value={formData.country} onChange={e => setFormData({...formData, country: e.target.value})} />
            </div>
          </div>
          <div className="mt-8 flex justify-end">
            <button 
              onClick={() => updateProfileMutation.mutate(formData)}
              disabled={updateProfileMutation.isLoading}
              className="bg-blue-600 text-white px-6 py-2.5 rounded-lg font-bold shadow-lg hover:bg-blue-700 flex items-center transition-all"
            >
              {updateProfileMutation.isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Save className="w-4 h-4 mr-2"/>} 
              Save Changes
            </button>
          </div>
        </div>
      )}

      {/* --- TAB 2: SECURITY & 2FA --- */}
      {activeTab === 'security' && (
        <div className="space-y-6 animate-in fade-in">
          
          {/* 2FA Section */}
          <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-lg font-bold text-slate-800 flex items-center">
                  <Smartphone className="w-5 h-5 mr-2 text-purple-600"/> Two-Factor Authentication
                </h3>
                <p className="text-slate-500 text-sm mt-1">Secure your account with Google Authenticator.</p>
              </div>
              <div>
                {profile?.is_2fa_enabled ? (
                  <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold flex items-center border border-green-200">
                    <CheckCircle className="w-3 h-3 mr-1"/> Enabled
                  </span>
                ) : (
                  <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-xs font-bold border border-slate-200">
                    Disabled
                  </span>
                )}
              </div>
            </div>

            {profile?.is_2fa_enabled ? (
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 flex items-center justify-between">
                <p className="text-sm text-slate-600">Your account is secure. You will need an OTP to login.</p>
                <button 
                  onClick={() => { if(window.confirm('Disable 2FA?')) disable2FAMutation.mutate(); }}
                  className="text-red-600 text-sm font-bold hover:underline"
                >
                  Disable 2FA
                </button>
              </div>
            ) : (
              <div className="bg-purple-50 p-6 rounded-lg border border-purple-100">
                <p className="text-sm text-purple-800 mb-4">
                  Add an extra layer of security. We recommend enabling 2FA for all staff accounts.
                </p>
                <button 
                  onClick={() => setup2FAMutation.mutate()}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-purple-700 shadow-sm"
                >
                  Enable 2FA Now
                </button>
              </div>
            )}
          </div>

          {/* Change Password */}
          <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
            <h3 className="text-lg font-bold text-slate-800 flex items-center mb-6">
              <Key className="w-5 h-5 mr-2 text-slate-500"/> Change Password
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">New Password</label>
                <input type="password" placeholder="Min 6 chars" className="w-full p-2.5 border rounded-lg" onChange={e => setPasswordData({...passwordData, new: e.target.value})} />
              </div>
              <button 
                onClick={() => {
                  if (passwordData.new.length < 6) return alert("Password too short");
                  changePasswordMutation.mutate({ password: passwordData.new });
                }}
                className="bg-slate-800 text-white px-6 py-2.5 rounded-lg font-bold hover:bg-slate-900"
              >
                Update Password
              </button>
            </div>
          </div>

        </div>
      )}

      {/* --- 2FA SETUP MODAL --- */}
      {show2FAModal && qrCodeData && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <h3 className="font-bold text-lg text-slate-800">Setup 2FA</h3>
              <button onClick={() => setShow2FAModal(false)}><X className="w-5 h-5 text-slate-400"/></button>
            </div>
            <div className="p-6 text-center space-y-6">
              <div className="bg-white p-2 rounded-lg border inline-block">
                <img src={qrCodeData.qr_code} alt="QR Code" className="w-48 h-48 object-contain" />
              </div>
              
              <div className="text-left space-y-2">
                <p className="text-sm text-slate-600">1. Open Google Authenticator.</p>
                <p className="text-sm text-slate-600">2. Scan the QR code above.</p>
                <p className="text-sm text-slate-600">3. Enter the 6-digit code below.</p>
              </div>

              <div>
                <input 
                  className="w-full text-center text-2xl tracking-widest font-mono p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  placeholder="000 000"
                  maxLength={6}
                  value={otp}
                  onChange={e => setOtp(e.target.value)}
                />
              </div>

              <button 
                onClick={() => enable2FAMutation.mutate({ secret: qrCodeData.secret, code: otp })}
                className="w-full bg-purple-600 text-white py-3 rounded-xl font-bold hover:bg-purple-700"
              >
                Verify & Enable
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default UserProfilePage;